import { Link, useNavigate } from "react-router-dom";
import { Activity, ChevronRight, LogOut, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";

export const AppShell = ({ breadcrumbs = [], children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const initials = (user?.name || user?.email || "U")
    .split(" ")
    .map((s) => s[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 border-b border-border no-print">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/dashboard" className="flex items-center gap-2 shrink-0" data-testid="app-logo-link">
              <span className="h-9 w-9 rounded-xl bg-primary text-primary-foreground flex items-center justify-center">
                <Activity className="h-5 w-5" />
              </span>
              <span className="font-heading font-semibold text-lg tracking-tight hidden sm:inline">ABA Clinica</span>
            </Link>
            {breadcrumbs.length > 0 && (
              <nav className="flex items-center gap-1 text-sm text-muted-foreground min-w-0" aria-label="breadcrumb">
                {breadcrumbs.map((b, i) => (
                  <span key={i} className="flex items-center gap-1 min-w-0">
                    <ChevronRight className="h-4 w-4 shrink-0" />
                    {b.to ? (
                      <Link to={b.to} className="hover:text-foreground truncate">{b.label}</Link>
                    ) : (
                      <span className="text-foreground font-medium truncate">{b.label}</span>
                    )}
                  </span>
                ))}
              </nav>
            )}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="gap-2 px-2" data-testid="user-menu-button">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={user?.picture || undefined} alt={user?.name} />
                  <AvatarFallback className="bg-accent text-accent-foreground text-xs">{initials}</AvatarFallback>
                </Avatar>
                <span className="hidden md:inline text-sm font-medium max-w-[160px] truncate">{user?.name}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="font-normal">
                <p className="text-sm font-medium truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem disabled className="gap-2">
                <User className="h-4 w-4" /> {user?.auth_provider === "google" ? "Account Google" : "Account email"}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleLogout} className="gap-2 text-destructive focus:text-destructive" data-testid="logout-button">
                <LogOut className="h-4 w-4" /> Esci
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">{children}</main>
    </div>
  );
};
